<script setup lang="ts">
type UserAccountButtonModeProps = {
  gaOptions?: Omit<DataLayerObject, 'event'>
}
const props = withDefaults(defineProps<UserAccountButtonModeProps>(), {})
const userStore = useUserStore()
const { accountUrl } = useSiteUrl()

const mergedGaOptions = {
  ...props.gaOptions,
  label: 'User Account',
  category: 'main navigation',
  type: 'UserAccount',
  group: 'actions menu',
}
</script>

<template>
  <div class="user-account-button">
    <template v-if="userStore.isLoggedIn">
      <Anchor class="user-account-button__my-account" :url="accountUrl" :ga-options="mergedGaOptions" :log-click="true">
        <div class="user-account-button__icon-wrap">
          <svg-icon name="mhj:user" />
          <client-only>
            <div v-if="userStore.initials" aria-hidden="true" class="user-account-button__initials">
              {{ userStore.initials }}
            </div>
          </client-only>
        </div>
        {{ $t('header.menu_drawer.buttons.my_account') }}
      </Anchor>
      <Anchor :url="accountUrl" :ga-options="mergedGaOptions" :log-click="true" class="user-account-button__logout">
        {{ $t('my_account.login_page.authenticated_view.log_out') }}
      </Anchor>
    </template>
    <template v-else>
      <div class="user-account-button__logged-out">
        <svg-icon name="mhj:user" />
        <Action
          variant="text"
          :ga-options="mergedGaOptions"
          :log-click="true"
          action="mhj-login-panel"
          class="user-account-button__login"
        >
          {{ $t('my_account.login_page.inputs.login.label') }}
        </Action>
        <Action
          variant="text"
          :ga-options="mergedGaOptions"
          :log-click="true"
          action="mhj-signup-panel"
          class="user-account-button__signup"
        >
          {{ $t('my_account.register.submit_button_text') }}
        </Action>
      </div>
    </template>
  </div>
</template>

<style lang="postcss">
.user-account-button {
  display: flex;
  gap: var(--space-50);
  width: fit-content;
  padding: var(--space-50) var(--space-30);

  .user-account-button__my-account {
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: var(--space-50);
    text-decoration: none;
    text-transform: uppercase;
  }

  .user-account-button__icon-wrap {
    position: relative;
  }

  .user-account-button__initials {
    position: absolute;
    right: 0;
    top: 50%;
    translate: 50% -47%;
    height: var(--space-50);
    width: var(--space-50);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-disclaimer);
    line-height: 1;
    padding-left: 0.08rem;
    letter-spacing: 0.01rem;
    color: var(--text-reverse);
    background-color: var(--surface-dark-10);
    border: 1px solid var(--surface-light-00);
    border-radius: var(--border-radius-full);
  }

  .user-account-button__logout {
    display: flex;
    align-items: center;
    font-size: var(--text-small);
  }

  .user-account-button__logged-out {
    display: flex;
    align-items: center;
    gap: var(--space-30);

    .action-wrapper {
      display: block;
      width: fit-content;
    }

    svg.icons {
      height: var(--space-60);
    }
  }

  .user-account-button__login {
    border-right: 1px solid black;
    padding-right: var(--space-30);
  }

  .user-account-button__signup,
  .user-account-button__login {
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
